import { Button } from '@components/button';
import { createForm, useCreateFormStore } from '@components/form';
import { hideModal, Modal, useModal } from '@components/modal';
import { PasswordStrengthBar } from '@components/password-strength-bar';
import { usePasswordValidations } from '@components/password-strength-bar/password-strength-bar';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useUpdatePassword } from 'api/users/hooks';

type ChangePasswordData = {
  oldPassword: string;
  password1: string;
  password2: string;
};

const Form = createForm<ChangePasswordData>();

export const useChangePasswordModal = () => useModal(ChangePasswordModal);

export const ChangePasswordModal = () => {
  const { t } = useScopedTranslate(locale);

  const formStore = useCreateFormStore();
  const setErrors = formStore.actions.setErrors;
  const formValues = formStore.useFormValues();
  const password1 = (formValues['password1'] as string) ?? '';
  const password2 = (formValues['password2'] as string) ?? '';

  const updatePasswordMutation = useUpdatePassword(setErrors, {
    onSuccess: () => {
      hideModal();
    },
  });

  const handleSave = (data: ChangePasswordData) => {
    const isPasswordsSame = (data.password1 ?? '') === (data.password2 ?? '');

    if (isPasswordsSame) {
      updatePasswordMutation.mutate(data);
    }
  };

  const { isAllValid, ValidationMessages } = usePasswordValidations(password1, password2);

  return (
    <Modal>
      <Modal.SimpleHeader title={t('changePassword')} />
      <Modal.Content>
        <Form store={formStore} onSubmit={handleSave}>
          <Stack space={6}>
            <Stack space={4}>
              <Form.PasswordField
                id="oldPassword"
                label={t('currentPassword')}
                validators={['required']}
              />
              <Form.PasswordField id="password1" label={t('newPassword')} validators={[]} />
              <PasswordStrengthBar password={password1} />
              <Form.PasswordField id="password2" label={t('confirmPassword')} validators={[]} />
              <ValidationMessages />
            </Stack>
            <Stack space={2} orientation="horizontal" justify="flex-end">
              <Button variant="secondary" onClick={hideModal} text={t('core.cancel')} />
              <Form.Submit
                text={t('core.save')}
                variant="primary-blue"
                isDisabled={updatePasswordMutation.isLoading || !isAllValid}
              />
            </Stack>
          </Stack>
        </Form>
      </Modal.Content>
    </Modal>
  );
};

export const locale = {
  en: {
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
  },
};
